import { FC } from 'react';
import {
  GenericField,
  WbCard,
  WbButton,
  WbCardContent,
  WbTagsArray,
  WbTooltip,
} from '@agilelab/plugin-wb-platform';
import { configApiRef, useApi } from '@backstage/core-plugin-api';
import { Box, Link, makeStyles, Typography, useTheme } from '@material-ui/core';
import { format, parseISO } from 'date-fns';
import { omit } from 'lodash';
import { useNavigate } from 'react-router';
import { useReleaseDetailPageContext } from './context/useReleaseDetailPageContext';
import { Label } from './Label';
import { ReleaseDeploymentStatus } from './ReleaseDeploymentStatus';

const useStyles = makeStyles(theme => ({
  ellipsis: {
    display: 'inline-block',
    maxWidth: 250,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  section: {
    display: 'grid',
    flexWrap: 'wrap',
    gridTemplateColumns: 'repeat(3, 1fr)',
    rowGap: '32px',
    padding: '16px 16px 16px 0px',
  },
  container: {
    display: 'flex',
    flexDirection: 'row',
    overflow: 'auto',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
  },
  rightSection: {
    paddingLeft: '16px',
    borderLeft: `1px solid ${theme.palette.grey[200]}`,
    padding: '16px 16px',
    rowGap: '0px',
    minWidth: '20%',
    display: 'grid',
    [theme.breakpoints.down('sm')]: {
      borderTop: `1px solid ${theme.palette.grey[200]}`,
      paddingLeft: '0px',
      borderLeft: 'none',
      minWidth: '100%',
    },
  },
}));

export const AboutCard: FC = () => {
  const classes = useStyles();
  const theme = useTheme();
  const { release, marketplaceLink, deploymentUnitStatusState } =
    useReleaseDetailPageContext();
  const navigate = useNavigate();
  const config = useApi(configApiRef);

  const marketplaceEnabled =
    config.getOptionalBoolean('mesh.marketplace.enabled') ?? true;

  const repoLink =
    release.metadata.annotations?.['backstage.io/source-location'].slice(4) ||
    release.metadata.repoUrl;
  let provisioningInfo =
    deploymentUnitStatusState.value?.provisioningDetails?.provisioningInfo
      ?.privateInfo;

  if (!marketplaceEnabled || !marketplaceLink) {
    provisioningInfo = omit(provisioningInfo, ['marketplaceInfo']);
  }

  const tags = release.metadata.tags?.map(tag => ({ tagFQN: tag })) ?? [];

  return (
    <WbCard
      title="About"
      actions={
        marketplaceEnabled && marketplaceLink ? (
          <WbTooltip title={marketplaceLink}>
            <WbButton
              label="View in Marketplace"
              variant="outlined"
              onClick={() => {
                if (marketplaceLink.startsWith('http')) {
                  window.location.href = marketplaceLink;
                  return;
                }
                navigate(marketplaceLink);
              }}
            />
          </WbTooltip>
        ) : undefined
      }
    >
      <WbCardContent>
        <Box className={classes.container}>
          <Box display="flex" flexDirection="column" flex="1">
            <Box className={classes.section}>
              <Label title="Name">
                <WbTooltip title={release.metadata.name}>
                  <Typography className={classes.ellipsis}>
                    {release.metadata.name}
                  </Typography>
                </WbTooltip>
              </Label>
              <Label title="Created At">
                {format(
                  parseISO(
                    release.metadata.isSnapshot && release.metadata.committedAt
                      ? release.metadata.committedAt
                      : release.metadata.createdAt,
                  ),
                  'yyyy/MM/dd HH:mm:ss',
                )}
              </Label>
              {release.metadata.releasedAt && (
                <Label title="Released At">
                  {format(
                    parseISO(release.metadata.releasedAt),
                    'yyyy/MM/dd HH:mm:ss',
                  )}
                </Label>
              )}
            </Box>
            <Box
              style={{
                borderTop: `1px solid ${theme.palette.grey[200]}`,
                borderBottom: `1px solid ${theme.palette.grey[200]}`,
              }}
              className={classes.section}
            >
              <Label title="Repository Link">
                <WbTooltip title={repoLink}>
                  <Link
                    href={repoLink}
                    target="_blank"
                    rel="noopener"
                    className={classes.ellipsis}
                  >
                    {repoLink}
                  </Link>
                </WbTooltip>
              </Label>
              {Object.values(provisioningInfo ?? {}).map(info => {
                return (
                  <GenericField
                    key={info.label ?? ''}
                    label={info.label ?? '-'}
                    href={info.href}
                    value={info.value ?? info.href}
                    type={info.type ?? 'string'}
                    textVariant="body2"
                    labelStyle={{ paddingBottom: '4px' }}
                  />
                );
              })}
            </Box>
            <Box className={classes.section}>
              <Label title="Tags">
                {!!tags.length ? (
                  <WbTagsArray tags={tags} />
                ) : (
                  <Typography>No tags</Typography>
                )}
              </Label>
            </Box>
          </Box>

          <Box className={classes.rightSection}>
            <ReleaseDeploymentStatus />
          </Box>
        </Box>
      </WbCardContent>
    </WbCard>
  );
};
